import React, { lazy, Suspense } from 'react'
import { Route, Redirect, Switch } from 'react-router-dom'
import { ConnectedRouter } from 'connected-react-router'
import { CSSTransition, SwitchTransition } from 'react-transition-group'
import { connect } from 'react-redux'
import { withAuthenticationRequired } from '@auth0/auth0-react'

import DataLayer from './data'
import Layout from 'layouts'

const routes = [
  // Apps pages
  // {
  //   path: '/apps/main',
  //   Component: lazy(() => import('pages/apps/main')),
  //   exact: true,
  //   restricted: true,
  // },
  // {
  //   path: '/apps/second',
  //   Component: lazy(() => import('pages/apps/second')),
  //   exact: true,
  //   restricted: true,
  // },
  // Home pages

  {
    path: '/',
    Component: lazy(() => import('pages/home')),
    exact: true,
    restricted: true,
  },
  {
    path: '/glossary',
    Component: lazy(() => import('components/swifteq/Home/Translate/Glossary/Glossary')),
    exact: true,
    restricted: true,
  },
  {
    path: '/custom-prompt',
    Component: lazy(() => import('components/swifteq/Home/Translate/CustomPrompt')),
    exact: true,
    restricted: true,
  },
  {
    path: '/cowriter',
    Component: lazy(() => import('pages/cowriter')),
    exact: true,
    restricted: true,
  },
  {
    path: '/mcp/subscribe',
    Component: lazy(() => import('pages/McpSubscribe')),
    exact: true,
    restricted: true,
  },
  // Init pages
  {
    path: '/init/connect',
    Component: lazy(() => import('pages/init/connect')),
    exact: true,
    restricted: true,
  },
  {
    path: '/init/processing',
    Component: lazy(() => import('pages/init/processing')),
    exact: true,
    restricted: true,
  },
  {
    path: '/init/expired',
    Component: lazy(() => import('pages/init/expired')),
    exact: true,
    restricted: true,
  },
  // Auth Pages
  {
    path: '/auth/answersearch',
    Component: lazy(() => import('pages/auth/answersearch')),
    exact: true,
    restricted: false,
  },
  {
    path: '/auth/login',
    Component: lazy(() => import('pages/auth/login')),
    exact: true,
    restricted: false,
  },
  {
    path: '/auth/login_iframe',
    Component: lazy(() => import('pages/auth/login_iframe')),
    exact: true,
    restricted: false,
  },
  {
    path: '/auth/reset_password',
    Component: lazy(() => import('pages/auth/reset_password')),
    exact: true,
    restricted: false,
  },
  {
    path: '/sidebar',
    Component: lazy(() => import('pages/auth/login_iframe')),
    exact: true,
    restricted: false,
  },
  {
    path: '/auth/macrosearch',
    Component: lazy(() => import('pages/auth/macrosearch')),
    exact: true,
    restricted: false,
  },
  {
    path: '/auth/translate',
    Component: lazy(() => import('pages/auth/translate')),
    exact: true,
    restricted: false,
  },
  {
    path: '/auth/cowriter',
    Component: lazy(() => import('pages/auth/cowriter')),
    exact: true,
    restricted: false,
  },
  {
    path: '/auth/exportPdfs',
    Component: lazy(() => import('pages/auth/export_pdfs')),
    exact: true,
    restricted: false,
  },
  {
    path: '/auth/mcpserver',
    Component: lazy(() => import('pages/auth/mcp_server')),
    exact: true,
    restricted: false,
  },
  {
    path: '/auth/easydoc',
    Component: lazy(() => import('pages/auth/easydoc')),
    exact: true,
    restricted: false,
  },
  {
    path: '/auth/macroexport',
    Component: lazy(() => import('pages/auth/macroexport')),
    exact: true,
    restricted: false,
  },
  {
    path: '/auth/mergedup',
    Component: lazy(() => import('pages/auth/mergedup')),
    exact: true,
    restricted: false,
  },
  {
    path: '/auth/removecc',
    Component: lazy(() => import('pages/auth/removecc')),
    exact: true,
    restricted: false,
  },
  {
    path: '/auth/attachments',
    Component: lazy(() => import('pages/auth/attachments')),
    exact: true,
    restricted: false,
  },
  {
    path: '/auth/redact',
    Component: lazy(() => import('pages/auth/redact')),
    exact: true,
    restricted: false,
  },
  {
    path: '/auth/lookup',
    Component: lazy(() => import('pages/auth/lookup')),
    exact: true,
    restricted: false,
  },
  {
    path: '/auth/triggers_gpt',
    Component: lazy(() => import('pages/auth/triggers_gpt')),
    exact: true,
    restricted: false,
  },
  {
    path: '/auth/classify',
    Component: lazy(() => import('pages/auth/classify')),
    exact: true,
    restricted: false,
  },
  {
    path: '/auth/document_parser',
    Component: lazy(() => import('pages/auth/document_parser')),
    exact: true,
    restricted: false,
  },
  {
    path: '/auth/resolveloop',
    Component: lazy(() => import('pages/auth/resolveloop')),
    exact: true,
    restricted: false,
  },
  {
    path: '/auth/404',
    Component: lazy(() => import('pages/auth/404')),
    exact: true,
    restricted: false,
  },
  {
    path: '/auth/500',
    Component: lazy(() => import('pages/auth/500')),
    exact: true,
    restricted: false,
  },
  {
    path: '/invite-users',
    Component: lazy(() => import('pages/InviteUsers')),
    exact: true,
    restricted: false,
  },
  {
    path: '/payment',
    Component: lazy(() => import('pages/PaymentPage')),
    exact: true,
    restricted: false,
  },
]

const mapStateToProps = ({ settings }) => ({
  routerAnimation: settings.routerAnimation,
})

const Router = ({ history, routerAnimation }) => {
  return (
    <ConnectedRouter history={history}>
      <DataLayer>
        <Layout>
          <Route
            render={state => {
              const { location } = state
              return (
                <SwitchTransition>
                  <CSSTransition
                    key={location.pathname}
                    appear
                    classNames={routerAnimation}
                    timeout={routerAnimation === 'none' ? 0 : 300}
                  >
                    <Switch location={location}>
                      {routes.map(({ path, Component, exact, restricted }) => {
                        const AuthenticatedComponent = restricted
                          ? withAuthenticationRequired(Component)
                          : Component
                        return (
                          <Route
                            path={path}
                            key={path}
                            exact={exact}
                            render={() => {
                              return (
                                <div className={routerAnimation}>
                                  <Suspense fallback={null}>
                                    <AuthenticatedComponent />
                                  </Suspense>
                                </div>
                              )
                            }}
                          />
                        )
                      })}
                      <Redirect to="/auth/404" />
                    </Switch>
                  </CSSTransition>
                </SwitchTransition>
              )
            }}
          />
        </Layout>
      </DataLayer>
    </ConnectedRouter>
  )
}

export default connect(mapStateToProps)(Router)
